import type { PaymentReceipt } from '@/services';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import { financeMethodLabel } from '@/shared/utils/finance-method-label.util';
import {
  BRAZIL_TIME_ZONE,
  formatDateTimeShortToBrazil,
  formatDateToBrazil,
} from '@/shared/utils/format-date.util';

export type PaymentReceiptTone = 'success' | 'processing' | 'warning' | 'error';

export type PaymentReceiptRow = { label: string; value: string };

export type PaymentReceiptView = {
  tone: PaymentReceiptTone;
  title: string;
  message: string;
  /** Resumo do seguro e do pagamento. */
  rows: PaymentReceiptRow[];
  /** O que vem depois: a próxima parcela e por onde ela chega. */
  next?: { title: string; description: string };
};

/** Link adulterado, antigo de outra chave ou de parcela que não existe. */
export const INVALID_RECEIPT_LINK_VIEW: PaymentReceiptView = {
  tone: 'warning',
  title: 'Link inválido',
  message:
    'Não encontramos este comprovante. Confira o endereço recebido ou fale com a sua corretora.',
  rows: [],
};

/**
 * `/payment` sem o token: não há parcela para consultar. A página só diz que
 * quem pagou não precisa pagar de novo.
 */
export const RECEIPT_WITHOUT_TOKEN_VIEW: PaymentReceiptView = {
  tone: 'processing',
  title: 'Estamos processando o seu pagamento',
  message:
    'Se você concluiu o pagamento, não é preciso fazer mais nada: a confirmação leva poucos instantes.',
  rows: [],
};

export const RECEIPT_LOAD_ERROR_VIEW: PaymentReceiptView = {
  tone: 'error',
  title: 'Não foi possível carregar o comprovante agora',
  message: 'Tente de novo em instantes.',
  rows: [],
};

/**
 * O que a página pública mostra ao segurado que voltou da Autra.
 *
 * `waitingTooLong`: a página parou de consultar e o pagamento segue sem
 * confirmação — o texto muda para não sugerir que ele pague de novo.
 */
export function buildPaymentReceiptView(
  receipt: PaymentReceipt,
  options: { waitingTooLong?: boolean } = {}
): PaymentReceiptView {
  const header = describeStatus(receipt, options.waitingTooLong === true);
  const showsNext =
    receipt.status === 'PAID' || receipt.status === 'PROCESSING';

  return {
    ...header,
    rows: summaryRows(receipt),
    next: showsNext ? describeNextInstallment(receipt) : undefined,
  };
}

function describeStatus(
  receipt: PaymentReceipt,
  waitingTooLong: boolean
): Pick<PaymentReceiptView, 'tone' | 'title' | 'message'> {
  switch (receipt.status) {
    case 'PAID':
      return {
        tone: 'success',
        title: 'Pagamento concluído!',
        message: receipt.insuredName
          ? `${receipt.insuredName}, recebemos o pagamento do seu seguro. Obrigado!`
          : 'Recebemos o pagamento do seu seguro. Obrigado!',
      };
    case 'PROCESSING':
      return {
        tone: 'processing',
        title: 'Estamos confirmando o seu pagamento',
        message: waitingTooLong
          ? 'A confirmação está demorando mais que o normal. Se você concluiu o pagamento, não é preciso pagar de novo: ele aparece aqui assim que for confirmado.'
          : 'Isso leva poucos instantes. Esta página se atualiza sozinha.',
      };
    case 'REFUSED':
      return {
        tone: 'error',
        title: 'Pagamento não aprovado',
        message:
          'O pagamento desta parcela não foi aprovado. Vamos enviar um novo link de pagamento para o seu e-mail; se preferir, fale com a sua corretora.',
      };
    default:
      return {
        tone: 'warning',
        title: 'Não encontramos a confirmação deste pagamento',
        message:
          'Se você já pagou, fale com a sua corretora para conferir o pagamento.',
      };
  }
}

function summaryRows(receipt: PaymentReceipt): PaymentReceiptRow[] {
  const validity =
    receipt.effectiveDate && receipt.expiryDate
      ? `${formatDateToBrazil(receipt.effectiveDate)} a ${formatDateToBrazil(receipt.expiryDate)}`
      : undefined;

  return [
    row('Seguro', receipt.productName),
    row('Apólice', receipt.policyNumber),
    row('Vigência', validity),
    row(
      receipt.singlePayment ? 'Pagamento' : 'Parcela',
      installmentText(receipt)
    ),
    row('Valor', formatCurrency(receipt.amount)),
    row(
      'Forma de pagamento',
      receipt.paymentMethod && financeMethodLabel(receipt.paymentMethod)
    ),
    row(
      'Pago em',
      receipt.paidAt &&
        formatDateTimeShortToBrazil(receipt.paidAt, BRAZIL_TIME_ZONE)
    ),
    row('Corretora', receipt.brokerName),
  ].filter((item): item is PaymentReceiptRow => Boolean(item));
}

/** Linha só com valor: o que a API não mandou não aparece. */
function row(label: string, value?: string): PaymentReceiptRow | undefined {
  return value ? { label, value } : undefined;
}

function installmentText(receipt: PaymentReceipt): string {
  if (receipt.singlePayment) {
    const times = receipt.cardInstallments ?? 1;
    return times > 1
      ? `Prêmio total, em até ${times}x no cartão`
      : 'Prêmio total';
  }
  if (receipt.installmentNumber == null) return '—';
  return receipt.installmentCount
    ? `${receipt.installmentNumber} de ${receipt.installmentCount}`
    : String(receipt.installmentNumber);
}

function describeNextInstallment(
  receipt: PaymentReceipt
): PaymentReceiptView['next'] {
  const next = receipt.nextInstallment;
  if (!next) return undefined;

  const dueDate = formatDateToBrazil(next.dueDate);
  const amount = formatCurrency(next.amount);

  if (next.channel === 'PIX_AUTOMATIC') {
    return {
      title: 'Próximas parcelas no PIX Automático',
      description: `A parcela ${next.number} vence em ${dueDate}, no valor de ${amount}, e é debitada da sua conta no vencimento. Se ainda não autorizou o PIX Automático, faça isso no app do seu banco.`,
    };
  }

  const count = receipt.installmentCount
    ? ` de ${receipt.installmentCount}`
    : '';
  const howItArrives = next.linkLeadDays
    ? `O link de pagamento chega no seu e-mail ${next.linkLeadDays} dias antes do vencimento.`
    : 'Você receberá o link de pagamento antes do vencimento.';

  return {
    title: `Próxima parcela: ${next.number}${count}`,
    description: `Vence em ${dueDate}, no valor de ${amount}. ${howItArrives}`,
  };
}
